// "Spending, no leads" — THE ONE RULE (Scott, 2026-10-29). PURE, no I/O, no clock.
//
// ⚠️⚠️ EVERY SURFACE THAT SHOWS A CPL OR RANKS ON ONE READS THIS FILE: the AM
// dashboard, the three Advertising Home pages, the creatives gallery, the
// wins & worries movers and the Slack digest. There is no second definition.
//
// THE RULE
//  · CPL is UNDEFINED when leads = 0. It is never $0 and a zero-lead spender is
//    never dropped from a ranking — a CPL of $0 reads as "leads were free",
//    the most flattering misstatement available (the series.ts rule, again).
//  · BRAND AVERAGE CPL = total spend ÷ total leads over ALL accounts under the
//    brand (never just the viewer's book), for the SAME channel, the SAME
//    window, and PER CURRENCY (AU and NZ are never mixed).
//  · "SPENDING, NO LEADS" = leads 0 AND spend ≥ the brand's average CPL (it
//    would normally have had at least one lead). A brand with no leads at all
//    in the window has no average, so the threshold falls back to
//    NO_LEADS_FALLBACK_SPEND. An account with NO brand has no brand to average
//    over and takes the same fallback (never an average over "all unbranded").
//  · ORDERING: a spending-no-leads account sorts WORSE than any finite CPL.
//  · MOVERS: leads → 0 is always a NEGATIVE move; 0 → some is a POSITIVE move.

import type { AdChannel, BrandWindowRow } from '../../types/advertisingHome.js';
import { NO_LEADS_FALLBACK_SPEND } from './guards.js';

export type { BrandWindowRow };

const KEY_SEP = '␟'; // a character no id or currency contains

export function brandAverageKey(brandId: string, channel: AdChannel, currency: string): string {
  return [brandId, channel, currency].join(KEY_SEP);
}

export interface BrandAverage {
  spend: number;
  leads: number;
  /** spend ÷ leads; null when the brand had no leads (or no spend) in the window. */
  cpl: number | null;
}

/** Brand → channel → currency → average. Built once from the wire rows. */
export class BrandAverages {
  private readonly byKey = new Map<string, BrandAverage>();

  constructor(rows: readonly BrandWindowRow[] = []) {
    for (const r of rows) {
      const key = brandAverageKey(r.brandId, r.channel, r.currency);
      const cur = this.byKey.get(key) ?? { spend: 0, leads: 0, cpl: null };
      cur.spend += r.spend;
      cur.leads += r.leads;
      this.byKey.set(key, cur);
    }
    for (const avg of this.byKey.values()) {
      avg.cpl = avg.leads > 0 && avg.spend > 0 ? avg.spend / avg.leads : null;
    }
  }

  /** null brand → null (no brand, no average). */
  get(brandId: string | null, channel: AdChannel, currency: string): BrandAverage | null {
    if (brandId === null) return null;
    return this.byKey.get(brandAverageKey(brandId, channel, currency)) ?? null;
  }

  cpl(brandId: string | null, channel: AdChannel, currency: string): number | null {
    return this.get(brandId, channel, currency)?.cpl ?? null;
  }
}

/**
 * Fold per-account window rows into per-brand totals over EVERY account in the
 * brand. ⚠️ `accounts` must be the whole brand membership, not the viewer's
 * book — passing the book is the bug this function's input type cannot catch,
 * so the SERVER builds the membership from the full roster (and a test pins it).
 */
export function aggregateBrandTotals(
  accounts: ReadonlyArray<{ id: string; brandAccountId: string | null }>,
  rows: ReadonlyArray<{
    accountId: string;
    channel: AdChannel;
    currency: string;
    spend: number;
    leads: number;
  }>,
  window: { from: string; to: string },
): BrandWindowRow[] {
  const brandOf = new Map(accounts.map((a) => [a.id, a.brandAccountId]));
  const sums = new Map<string, BrandWindowRow>();
  for (const r of rows) {
    const brandId = brandOf.get(r.accountId) ?? null;
    if (brandId === null) continue;
    const key = brandAverageKey(brandId, r.channel, r.currency);
    const cur = sums.get(key) ?? {
      brandId,
      channel: r.channel,
      currency: r.currency,
      from: window.from,
      to: window.to,
      spend: 0,
      leads: 0,
    };
    cur.spend += r.spend;
    cur.leads += r.leads;
    sums.set(key, cur);
  }
  return [...sums.values()]
    .map((r) => ({ ...r, spend: round2(r.spend), leads: round2(r.leads) }))
    .sort(
      (a, b) =>
        a.brandId.localeCompare(b.brandId) ||
        a.channel.localeCompare(b.channel) ||
        a.currency.localeCompare(b.currency),
    );
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** The spend an account must reach, with no leads, to count as "spending, no leads". */
export function noLeadsThreshold(brandCpl: number | null): {
  threshold: number;
  usedFallback: boolean;
} {
  return brandCpl !== null && brandCpl > 0
    ? { threshold: brandCpl, usedFallback: false }
    : { threshold: NO_LEADS_FALLBACK_SPEND, usedFallback: true };
}

export type CplVerdict =
  /** Nothing spent and no leads: there is no CPL and nothing to flag. */
  | { kind: 'none' }
  /** Leads > 0: a real, finite CPL. */
  | { kind: 'cpl'; cpl: number }
  /** Leads 0, spend ≥ the brand's average CPL (or the fallback): the red case. */
  | { kind: 'no_leads'; spend: number; threshold: number; usedFallback: boolean }
  /** Leads 0 but spend below the threshold: undefined CPL, not yet worrying. */
  | { kind: 'no_leads_low_spend'; spend: number; threshold: number; usedFallback: boolean };

/**
 * The single classification. ⚠️ Never returns a CPL for leads ≤ 0, so no
 * caller can print `$0`; callers switch on `kind`.
 */
export function classifyCpl(input: {
  spend: number;
  leads: number;
  brandCpl: number | null;
}): CplVerdict {
  const { spend, leads, brandCpl } = input;
  if (leads > 0) {
    return spend > 0 ? { kind: 'cpl', cpl: spend / leads } : { kind: 'none' };
  }
  if (spend <= 0) return { kind: 'none' };
  const { threshold, usedFallback } = noLeadsThreshold(brandCpl);
  return spend >= threshold
    ? { kind: 'no_leads', spend, threshold, usedFallback }
    : { kind: 'no_leads_low_spend', spend, threshold, usedFallback };
}

export const isSpendingNoLeads = (v: CplVerdict): v is Extract<CplVerdict, { kind: 'no_leads' }> =>
  v.kind === 'no_leads';

/**
 * Sort weight, WORST first. A flagged spender is worse than any finite CPL;
 * among them, more spend is worse; among finite CPLs, higher is worse. The
 * unflagged zero-lead low spenders sit between "bad CPL" and "nothing":
 * undefined, but cheaper than a flagged one and with no CPL to compare.
 */
export function compareVerdictsWorstFirst(a: CplVerdict, b: CplVerdict): number {
  const rank = (v: CplVerdict): number =>
    v.kind === 'no_leads' ? 3 : v.kind === 'cpl' ? 2 : v.kind === 'no_leads_low_spend' ? 1 : 0;
  const ra = rank(a);
  const rb = rank(b);
  if (ra !== rb) return rb - ra;
  if (a.kind === 'cpl' && b.kind === 'cpl') return b.cpl - a.cpl;
  if (
    (a.kind === 'no_leads' || a.kind === 'no_leads_low_spend') &&
    (b.kind === 'no_leads' || b.kind === 'no_leads_low_spend')
  ) {
    return b.spend - a.spend;
  }
  return 0;
}

/** The cell text. ⚠️ `formatMoney` is only ever handed a real positive CPL. */
export function cplCellText(v: CplVerdict, formatMoney: (n: number) => string): string {
  switch (v.kind) {
    case 'cpl':
      return formatMoney(v.cpl);
    case 'no_leads':
      return `No leads · ${formatMoney(v.spend)} spent`;
    case 'no_leads_low_spend':
      return `No leads yet · ${formatMoney(v.spend)} spent`;
    case 'none':
      return '—';
  }
}

// ── Movers ────────────────────────────────────────────────────────────────

export type MoverKind =
  /** Leads went from some to 0 — ALWAYS a negative move. */
  | 'leads_gone'
  /** Leads went from 0 to some — a positive move ("Leads back"). */
  | 'leads_back'
  /** Both periods have leads: an ordinary CPL change. */
  | 'cpl_change';

export interface MoverWindow {
  leads: number;
  spend: number;
}

/**
 * The transition between two windows. Null when neither period had leads (no
 * move to speak of). ⚠️ A zero-lead transition needs spend in the AFTER window
 * for `leads_gone` (an account that simply stopped spending went quiet, which
 * the no-spend alert owns) and is never filtered by the lead-volume floor.
 */
export function classifyMover(before: MoverWindow, after: MoverWindow): MoverKind | null {
  if (before.leads > 0 && after.leads <= 0) return after.spend > 0 ? 'leads_gone' : null;
  if (before.leads <= 0 && after.leads > 0) return 'leads_back';
  if (before.leads > 0 && after.leads > 0) return 'cpl_change';
  return null;
}
